import { Component, Input, ChangeDetectionStrategy, OnInit, OnChanges, OnDestroy, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { BackendApiService } from '../../../../../services/backend-api.service';

export interface HistoryItem {
  id: string;
  action: string;
  user: string;
  timestamp: Date | string;
  details?: string;
  icon?: string;
}

@Component({
    selector: 'app-task-history-tab',
    templateUrl: './task-history-tab.component.html',
    styleUrls: ['./task-history-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TaskHistoryTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() history: HistoryItem[] = [];
  @Input() taskId?: string;
  @Input() taskModel?: any; // Task model containing reference and module

  // Module ID - will be set from taskModel
  public moduleID: number = 7;

  // Activity log data
  search: string = '';
  private searchInput = new Subject<string>();
  private readonly debounceTimeMs = 500;
  private subscription = new Subscription();
  showLoadMoreOption: boolean = false;
  activityLogList: any[] = [];
  activityLogMeta: any;
  param = { per_page: 5, page: 1 };
  requestIsInProgress: boolean = false;

  constructor(
    private backendApiService: BackendApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Only fetch if we have the necessary data
    if (this.taskId && this.taskModel) {
      this.getActivityLog(true);
    }
  }

  /**
   * Flagging, unflagging or adding a reason writes a new history entry on the server, so reload the
   * list from the first page whenever the task's flag fields change (not on the first binding, which
   * ngOnInit already loads).
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['taskModel'];
    if (!change || change.firstChange) {
      return;
    }
    const prev = change.previousValue;
    const curr = change.currentValue;
    if (
      prev?.is_flagged !== curr?.is_flagged ||
      prev?.flag_text !== curr?.flag_text ||
      prev?.unflag_text !== curr?.unflag_text
    ) {
      this.param.page = 1;
      this.getActivityLog(true);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Get activity log from API
   */
  getActivityLog(isService: any) {
    if (!this.taskId || !this.taskModel) return;

    // Get module_id and subject_id from task's reference object
    const moduleId = this.moduleID.toString();
    const subjectId =this.taskModel?.id;

    if (!moduleId || !subjectId) {
      console.warn('[TASK-HISTORY] Missing module or reference data from task model');
      return;
    }

    this.requestIsInProgress = true;
    // A full reload starts from page 1: drop the stale "Load More" until the new response says there is more.
    if (isService) {
      this.showLoadMoreOption = false;
    }

    // Build query string for GET request
    const queryParams = new URLSearchParams({
      module_id: moduleId.toString(),
      subject_id: subjectId.toString(),
      sort_by: 'id',
      sort_order: 'DESC',
      per_page: this.param.per_page.toString(),
      page: this.param.page.toString(),
      search: this.search,
    });

    const activityLogSubscription = this.backendApiService
      .getApi(`activity/log?${queryParams.toString()}`)
      .subscribe({
        next: (response: any) => {
          if (isService) {
            this.activityLogList = [];
          }
          this.activityLogList = this.activityLogList
            ? [...this.activityLogList, ...response.data]
            : [...response.data];

          this.activityLogMeta = response?.meta;
          this.updateLoadMoreOption();

          // Transform API data to HistoryItem format
          this.history = this.transformActivityLogToHistory(this.activityLogList);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading activity log:', err);
          this.requestIsInProgress = false;
          this.cdr.markForCheck();
        },
        complete: () => {
          this.requestIsInProgress = false;
          this.cdr.markForCheck();
        },
      });
    this.subscription.add(activityLogSubscription);
  }

  /**
   * Transform API activity log data to HistoryItem format
   */
  private transformActivityLogToHistory(logs: any[]): HistoryItem[] {
    return logs.map(log => ({
      id: log.Activity_id?.toString() || log.id?.toString() || '',
      action: log.event || log.log_name || 'updated',
      user: log.perform_by?.full_name ||
            (log.perform_by?.first_name + ' ' + log.perform_by?.last_name) ||
            'Unknown User',
      timestamp: log.created_at_raw || log.created_at || new Date(),
      details: log.description ? this.stripHtml(log.description) : undefined
    }));
  }

  /**
   * Strip HTML tags from description
   */
  private stripHtml(html: string): string {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  /**
   * Load more data (pagination)
   */
  loadMoreData() {
    this.param.page += 1;
    this.updateLoadMoreOption();
    if (this.showLoadMoreOption) {
      this.getActivityLog(false);
    }
  }

  /**
   * Update load more option visibility
   */
  private updateLoadMoreOption(): void {
    this.showLoadMoreOption =
      this.activityLogMeta &&
      this.activityLogMeta.current_page < this.activityLogMeta.last_page;
  }

  /**
   * Get Material Icon name for event type
   */
  getEventIcon(action: string): string {
    const iconMap: { [key: string]: string } = {
      'created': 'add_circle',
      'assigned': 'person_add',
      'checklist': 'check_circle',
      'milestone': 'flag',
      'status': 'swap_horiz',
      'status_changed': 'swap_horiz',
      'comment': 'comment',
      'commented': 'comment',
      'attachment': 'attach_file',
      'file_uploaded': 'attach_file',
      'completed': 'done_all',
      'updated': 'edit'
    };
    return iconMap[action.toLowerCase()] || 'info';
  }

  /**
   * Get CSS class for event icon color based on event type
   */
  getEventIconClass(action: string): string {
    const classMap: { [key: string]: string } = {
      'created': 'event-icon-created',
      'assigned': 'event-icon-assigned',
      'checklist': 'event-icon-checklist',
      'milestone': 'event-icon-milestone',
      'status': 'event-icon-status',
      'status_changed': 'event-icon-status_changed',
      'comment': 'event-icon-comment',
      'commented': 'event-icon-commented',
      'attachment': 'event-icon-attachment',
      'file_uploaded': 'event-icon-file_uploaded',
      'completed': 'event-icon-completed',
      'updated': 'event-icon-updated'
    };
    return classMap[action.toLowerCase()] || 'event-icon-default';
  }

  /**
   * Format action name for display (e.g., "status_changed" -> "Status changed")
   */
  formatAction(action: string): string {
    const actionMap: { [key: string]: string } = {
      'created': 'created this task',
      'assigned': 'assigned task',
      'status_changed': 'changed status',
      'checklist': 'updated checklist',
      'milestone': 'updated milestone',
      'comment': 'commented',
      'commented': 'commented',
      'attachment': 'added attachment',
      'file_uploaded': 'uploaded file',
      'completed': 'completed task',
      'updated': 'updated task'
    };

    const formatted = actionMap[action.toLowerCase()];
    if (formatted) {
      return formatted;
    }

    // Fallback: Convert snake_case to Title Case
    return action
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  /**
   * Format timestamp as relative time (e.g., "2 hours ago")
   */
  formatEventTime(timestamp: Date | string): string {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) {
      return 'Just now';
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    }
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }
    if (diffDays < 30) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }

    // For older events, show formatted date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }

  /**
   * Legacy method for backward compatibility
   * @deprecated Use getEventIcon instead
   */
  getActionIcon(action: string): string {
    return this.getEventIcon(action);
  }

  /**
   * Format timestamp as full date (for tooltips or detailed view)
   */
  formatTimestamp(timestamp: Date | string): string {
    const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
